/**
 * Intake Queue Page
 *
 * Shows items in the intake queue (lifecycle_stage === 'intake'), ready for continued item work.
 * All work objects are items from creation; queue/readiness determines eligibility.
 *
 * DATA SOURCE: items table
 * FILTER: lifecycle_stage === 'intake' (queue classification, not structural state)
 */

import { useState, useMemo, useCallback, useEffect } from 'react';
import { Inbox, Hammer, Eye, Users, ListChecks, Loader2, Lock, Building2, Route, ChevronDown, User } from 'lucide-react';
import { PageHeader } from '../../components/prioritization/PageHeader';
import { Link, useNavigate } from 'react-router-dom';
import { getCurrentWorkspaceId } from '../../lib/supabaseSync';
import { getSupabaseClient } from '../../lib/supabaseClient';
import { useTenantStore } from '../../stores/prioritization/tenantStore';
import { useUIStore } from '../../stores/prioritization/uiStore';
import { useUserStore } from '../../stores/prioritization/userStore';
import { useEnterpriseIdentityStore } from '../../stores/prioritization/enterpriseIdentityStore';
import { useItemsStore } from '../../stores/prioritization/itemsStore';
import { useTriageStore } from '../../stores/prioritization/triageStore';
import { TriageStatusBadge } from '../../components/prioritization/TriageStatusBadge';
import { QUEUE_VIEW_FILTERS } from '../../lib/itemLifecycle';
import { createEmptyQuickChecks } from '../../types/prioritization/triage';
import { 
  CollectionView,
  type FacetDefinition,
  type SortOption,
  type ItemRenderProps,
} from '../../components/collection';

// Legacy-compatible display statuses for this intake queue view.
type SubmissionStatus = 'INTAKE_PENDING' | 'SHAPING' | 'REDIRECTED' | 'READY' | 'RETURNED' | 'REJECTED';

/**
 * IntakeItem - represents an item in the intake queue (queue classification: intake)
 */
interface IntakeItem {
  id: string;
  title: string;
  description: string;
  createdBy: string;
  createdAt: Date;
  lifecycle_state: 'item';
  lifecycle_stage?: string;
  detailedState?: string;
  tags: string[];
  
  // Optional metadata
  category?: string;
  priority?: string;
  orgUnitId?: string;
  orgUnitName?: string;
  queueId?: string;
  
  // Stewardship routing
  assignedStewardId?: string;
  assignedStewardName?: string;
  needsTriage?: boolean;
  
}

// View model kept for the collection renderer while the page stays item-backed.
interface IdeaSubmission {
  submissionId: string;
  title: string;
  description: string;
  submittedBy: string;
  submitterName: string;
  submittedAt: string;
  status: SubmissionStatus;
  category?: string;
  priority?: string;
  shapingItemId?: string;
  orgUnitId?: string;
  orgUnitName?: string;
  queueId?: string;
  assignedStewardId?: string;
  assignedStewardName?: string;
  needsTriage?: boolean;
}

// ============================================================================
// Submission Card Component
// ============================================================================

interface SubmissionCardProps {
  submission: IdeaSubmission;
  orgUnitName?: string;
  canShape: boolean;
  canRoute: boolean;
  isShaping: boolean;
  onShape: () => void;
  onRoute: (queueId: string) => void;
  availableQueues: { id: string; label: string }[];
  /** Optional linked item id for triage status lookup */
  linkedCandidateId?: string;
}

function SubmissionCard({ submission, orgUnitName, canShape, canRoute, isShaping, onShape, onRoute, availableQueues, linkedCandidateId }: SubmissionCardProps) {
  const [showRouteMenu, setShowRouteMenu] = useState(false);
  const canStartItemWork = canShape && submission.status !== 'REDIRECTED';
  
  const priorityColors: Record<string, string> = {
    critical: 'bg-[var(--coral-dim)] text-[var(--coral)]',
    high: 'bg-[var(--mustard-dim)] text-[var(--mustard)]',
    medium: 'bg-[var(--mustard-dim)] text-[var(--mustard)]',
    low: 'bg-[var(--hover-tint)] text-[var(--text-pri)]',
  };
  
  const currentQueue = availableQueues.find(q => q.id === submission.queueId);
  
  return (
    // Wraps on a phone: the actions drop below the submission instead of
    // pushing the page wider than the screen.
    <div className={`flex flex-wrap items-start justify-between gap-y-3 ${!canShape ? 'opacity-60' : ''}`}>
      <div className="min-w-0 flex-[1_1_16rem]">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <h3 className="font-medium text-[var(--text-pri)]">{submission.title}</h3>
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
            submission.status === 'INTAKE_PENDING'
              ? 'bg-[var(--mustard-dim)] text-[var(--mustard)]'
              : submission.status === 'REDIRECTED'
                ? 'bg-[var(--slate-dim)] text-[var(--text-sec)]'
                : 'bg-[var(--slate-dim)] text-[var(--text-sec)]'
          }`}>
            {submission.status === 'INTAKE_PENDING'
              ? 'Pending'
              : submission.status === 'REDIRECTED'
                ? 'Redirected'
                : 'In Item Work'}
          </span>
          {submission.category && (
            <span className="px-2 py-1 rounded-full text-xs font-medium bg-[var(--slate-dim)] text-[var(--text-sec)]">
              {submission.category}
            </span>
          )}
          {submission.priority && (
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${priorityColors[submission.priority.toLowerCase()] || 'bg-[var(--hover-tint)] text-[var(--text-pri)]'}`}>
              {submission.priority}
            </span>
          )}
          {orgUnitName && (
            <span className="px-2 py-1 rounded-full text-xs font-medium bg-[var(--slate-dim)] text-[var(--text-sec)] flex items-center gap-1">
              <Building2 className="h-3 w-3" />
              {orgUnitName}
            </span>
          )}
          {currentQueue && (
            <span className="px-2 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-700 flex items-center gap-1">
              <Route className="h-3 w-3" />
              {currentQueue.label}
            </span>
          )}
          {submission.assignedStewardName && (
            <span className="px-2 py-1 rounded-full text-xs font-medium bg-cyan-100 text-cyan-700 flex items-center gap-1">
              <User className="h-3 w-3" />
              {submission.assignedStewardName}
            </span>
          )}
          {submission.needsTriage && (
            <span className="px-2 py-1 rounded-full text-xs font-medium bg-[var(--mustard-dim)] text-[var(--mustard)] flex items-center gap-1">
              Needs Triage
            </span>
          )}
          {linkedCandidateId && (
            <TriageStatusBadge itemId={linkedCandidateId} size="sm" />
          )}
          {!canShape && (
            <span className="px-2 py-1 rounded-full text-xs font-medium bg-[var(--coral-dim)] text-[var(--coral)] flex items-center gap-1">
              <Lock className="h-3 w-3" />
              No access
            </span>
          )}
        </div>
        <p className="text-sm text-[var(--text-sec)] mt-1 mb-2">{submission.description}</p>
        <div className="flex items-center gap-3 mt-2 text-xs text-[var(--text-ter)]">
          <span className="font-medium text-[var(--text-sec)]">Submitted by: {submission.submitterName || submission.submittedBy}</span>
          <span>•</span>
          <span>{new Date(submission.submittedAt).toLocaleDateString()}</span>
        </div>
      </div>
      
      <div className="ml-auto flex flex-wrap gap-2 pl-4">
        {/* Route to queue dropdown - only for stewards with routing permissions */}
        {canRoute && (
          <div className="relative">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowRouteMenu(!showRouteMenu);
              }}
              className="btn btn-secondary text-sm flex items-center gap-1"
            >
              <Route className="h-4 w-4" />
              Route
              <ChevronDown className="h-3 w-3" />
            </button>
            
            {showRouteMenu && (
              <>
                <div 
                  className="fixed inset-0 z-10" 
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowRouteMenu(false);
                  }} 
                />
                <div className="absolute right-0 top-full mt-1 w-48 bg-[var(--bg-raised)] shadow-lg border py-1 z-20">
                  <div className="px-3 py-1.5 text-xs font-medium text-[var(--text-ter)] border-b">
                    Route to Queue
                  </div>
                  {availableQueues.length === 0 ? (
                    <div className="px-3 py-2 text-sm text-[var(--text-ter)] italic">
                      No queues configured
                    </div>
                  ) : (
                    availableQueues.map((queue) => (
                      <button
                        key={queue.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          onRoute(queue.id);
                          setShowRouteMenu(false);
                        }}
                        className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-[var(--surface)] ${
                          submission.queueId === queue.id ? 'bg-[var(--surface)] font-medium' : ''
                        }`}
                      >
                        <span className="w-2 h-2 rounded-full bg-primary-500" />
                        {queue.label}
                        {submission.queueId === queue.id && (
                          <span className="ml-auto text-xs text-[var(--text-ter)]">Current</span>
                        )}
                      </button>
                    ))
                  )}
                </div>
              </>
            )}
          </div>
        )}
        
        <button
          onClick={(e) => {
            e.stopPropagation();
            onShape();
          }}
          disabled={isShaping || !canStartItemWork}
          className="btn btn-primary text-sm flex items-center gap-1 disabled:opacity-50"
        >
          {isShaping ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Starting...
            </>
          ) : (
            <>
              <Hammer className="h-4 w-4" />
              Start Item Work
            </>
          )}
        </button>
        {submission.shapingItemId && (
          <Link
            to={`/prioritization/shape/queue`}
            state={{ candidateId: submission.shapingItemId }}
            className="btn btn-secondary text-sm flex items-center gap-1"
            onClick={(e) => e.stopPropagation()}
          >
            <Eye className="h-4 w-4" />
            View Item
          </Link>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export default function IntakeQueue() {
  const navigate = useNavigate();
  
  const can = useUserStore((state) => state.can);
  const canTriage = can('items.triage');
  const canShape = can('items.edit');
  
  const [shapingId, setShapingId] = useState<string | null>(null);
  const [selectedQueueFilter, setSelectedQueueFilter] = useState<string>('all');
  const [showNonTriagedItems, setShowNonTriagedItems] = useState(false);
  
  const [intakeItems, setIntakeItems] = useState<IntakeItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const currentWorkspaceId = useTenantStore((state) => state.currentWorkspaceId);

  const fetchIntakeItems = useCallback(async () => {
    try {
      setLoading(true);
      const workspaceId = getCurrentWorkspaceId();
      if (!workspaceId) {
        setIntakeItems([]);
        setLoading(false);
        return;
      }
      const supabase = getSupabaseClient();

      const { data, error: fetchError } = await supabase
        .from('items')
        .select('*')
        .eq('workspace_id', workspaceId)
        .eq('exit_status', 'active')
        .order('created_at', { ascending: false });

      if (fetchError) {
        console.error('[IntakeQueue] Supabase error:', fetchError);
        setError(fetchError.message);
        return;
      }

      const createdByUserIds = [...new Set((data || []).map((c: any) => c.created_by_user_id).filter(Boolean))];
      const createdByDisplayMap: Record<string, string> = {};
      if (createdByUserIds.length > 0) {
        const { data: users } = await supabase
          .from('users')
          .select('id, name, email')
          .in('id', createdByUserIds);
        for (const u of users || []) {
          createdByDisplayMap[u.id] = u.name || u.email?.split('@')[0] || 'User';
        }
      }

      const items: IntakeItem[] = (data || []).map((c: any) => ({
        id: c.id,
        title: c.title || 'Untitled',
        description: c.description || '',
        createdBy: c.created_by_user_id
          ? (createdByDisplayMap[c.created_by_user_id] || c.created_by || 'User')
          : 'Anonymous',
        createdAt: new Date(c.created_at),
        lifecycle_state: 'item',
        exit_status: c.exit_status ?? 'active',
        tags: c.tags || [],
        category: c.tags?.[0],
        queueId: c.queue_id || undefined,
        orgUnitId: c.org_unit_id || undefined,
      }));

      setIntakeItems(items);
    } catch (err) {
      console.error('[IntakeQueue] Fetch error:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [currentWorkspaceId]);

  useEffect(() => {
    fetchIntakeItems();
  }, [fetchIntakeItems]);
  
  // Triage status for filtering
  const getTriageAssessment = useTriageStore((state) => state.getTriageAssessment);
  const submitTriageAssessment = useTriageStore((state) => state.submitTriageAssessment);
  const addNotification = useUIStore((state) => state.addNotification);
  
  // Enterprise permissions
  const currentUserProfile = useEnterpriseIdentityStore((state) => state.currentUserProfile);
  const loadDemoData = useEnterpriseIdentityStore((state) => state.loadDemoData);
  const orgUnitsMap = useEnterpriseIdentityStore((state) => state.orgUnits);
  
  const isEnterpriseLoaded = !!currentUserProfile;
  const orgUnits = useMemo(() => Array.from(orgUnitsMap.values()), [orgUnitsMap]);
  
  // Get available queues (org units with queue enabled)
  const availableQueues = useMemo(() => {
    return orgUnits
      .filter(ou => ou.queueSettings?.enabled)
      .map(ou => ({ id: ou.id, label: ou.displayName }));
  }, [orgUnits]);
  
  const canShapeIn = useCallback((_orgUnitId: string): boolean => can('items.edit'), [can]);
  
  // Check if user has org-wide steward/triage permissions (effective permissions from all roles)
  const canRouteItems = useMemo(() => canTriage, [canTriage]);
  
  // Handle routing through the canonical triage redirect path.
  const handleRoute = useCallback(async (itemId: string, queueId: string) => {
    try {
      const queueLabel = availableQueues.find(q => q.id === queueId)?.label || queueId;
      await submitTriageAssessment(itemId, {
        itemId,
        quickChecks: createEmptyQuickChecks(),
        outcome: 'redirect',
        redirectTarget: queueId,
      });
      
      // Refresh from Supabase to get updated data
      await fetchIntakeItems();
      await useItemsStore.getState().loadItemById(itemId);
      
      addNotification({
        type: 'success',
        title: 'Routed',
        message: `Routed to ${queueLabel}`,
      });
    } catch (err) {
      console.error('Failed to route submission:', err);
      addNotification({
        type: 'error',
        title: 'Error',
        message: `Failed to route submission: ${err instanceof Error ? err.message : 'Unknown error'}`,
      });
    }
  }, [addNotification, availableQueues, fetchIntakeItems, submitTriageAssessment]);
  
  const orgUnitMap = useMemo(() => {
    return new Map(orgUnits.map(ou => [ou.id, ou]));
  }, [orgUnits]);
  // Filter intake items by selected queue and triage status.
  const filteredItems = useMemo(() => {
    let filtered = intakeItems;
    
    // Queue filter
    if (selectedQueueFilter !== 'all') {
      filtered = filtered.filter(item => 
        item.queueId === selectedQueueFilter || 
        item.category === selectedQueueFilter ||
        item.tags.includes(selectedQueueFilter)
      );
    }
    
    // Triage status filter - show only triaged items by default.
    // Intake Queue shows items that have passed triage and are available for continued item work.
    if (!showNonTriagedItems) {
      filtered = filtered.filter(item => {
        // Check if the item has been triaged.
        const assessment = getTriageAssessment(item.id);
        // Only items explicitly pursued are ready for continued item work.
        return assessment?.outcome === 'pursue';
      });
    }
    
    return filtered;
  }, [intakeItems, selectedQueueFilter, showNonTriagedItems, getTriageAssessment]);
  
  // Convert filtered items to the collection view model.
  const filteredSubmissions: IdeaSubmission[] = useMemo(() => 
    filteredItems.map(item => {
      const assessment = getTriageAssessment(item.id);
      const status: SubmissionStatus =
        assessment?.outcome === 'redirect'
          ? 'REDIRECTED'
          : assessment?.outcome === 'pursue'
            ? 'READY'
            : 'INTAKE_PENDING';

      return ({
      submissionId: item.id,
      title: item.title,
      description: item.description,
      submittedBy: item.createdBy,
      submitterName: item.createdBy,
      submittedAt: item.createdAt.toISOString(),
      status,
      category: item.category,
      priority: item.priority,
      shapingItemId: item.id,
      orgUnitId: item.orgUnitId,
      orgUnitName: item.orgUnitName,
      queueId: item.queueId,
      assignedStewardId: item.assignedStewardId,
      assignedStewardName: item.assignedStewardName,
      needsTriage: item.needsTriage,
    });
    }),
    [filteredItems, getTriageAssessment]
  );
  
  // Stats (using filtered items from canonical source)
  const stats = useMemo(() => ({
    pending: filteredSubmissions.filter(item => item.status === 'INTAKE_PENDING').length,
    shaping: filteredSubmissions.filter(item => item.status === 'SHAPING').length,
    redirected: filteredSubmissions.filter(item => item.status === 'REDIRECTED').length,
    total: filteredSubmissions.length,
    source: QUEUE_VIEW_FILTERS.intake.table,
  }), [filteredSubmissions]);
  
  // Open an item for continued item work.
  const handleShapeItem = useCallback(async (item: IntakeItem) => {
    setShapingId(item.id);

    const itemsStore = useItemsStore.getState();
    let storeItem = itemsStore.getItemById(item.id);
    if (!storeItem) {
      console.warn('[IntakeQueue] Item not in store; loading then retrying', { itemId: item.id });
      await itemsStore.loadItemById(item.id);
      storeItem = itemsStore.getItemById(item.id);
      if (!storeItem) return;
    }

    // Ensure it shows up in the item-work list even when showNonTriaged=false.
    await itemsStore.updateItem(item.id, { detailedState: 'SHAPING' });

    navigate(`/prioritization/shape/queue?candidateId=${item.id}`, { state: { candidateId: item.id } });

    addNotification({
      type: 'success',
      title: 'Opening item work',
      message: `Opening "${item.title}" for item work`,
      duration: 2000
    });

    setShapingId(null);
  }, [navigate, addNotification]);
  
  // Backward-compatible handler for the collection renderer.
  const handleShape = useCallback(async (submission: IdeaSubmission) => {
    // Find the corresponding intake item
    const item = intakeItems.find(i => i.id === submission.submissionId || i.id === submission.shapingItemId);
    if (item) {
      handleShapeItem(item);
    } else {
      const id = submission.shapingItemId || submission.submissionId;
      navigate(`/prioritization/shape/queue?candidateId=${id}`, { state: { candidateId: id } });
    }
  }, [intakeItems, handleShapeItem, navigate]);
  
  
  // Define facets - MUST be before any early returns to respect Rules of Hooks
  const facets: FacetDefinition<string>[] = useMemo(() => {
    const statusFacet: FacetDefinition<string> = {
      key: 'status',
      label: 'Status',
      icon: ListChecks,
      options: [
        { value: 'INTAKE_PENDING', label: 'Pending', count: stats.pending },
        { value: 'SHAPING', label: 'In Item Work', count: stats.shaping },
        { value: 'REDIRECTED', label: 'Redirected', count: stats.redirected },
      ],
    };
    
    const orgUnitOptions = orgUnits
      .filter(ou => ou.type === 'department' || ou.type === 'team')
      .map(ou => ({ value: ou.id, label: ou.displayName }));
    
    if (orgUnitOptions.length > 0) {
      return [
        statusFacet,
        {
          key: 'orgUnitId',
          label: 'Department',
          icon: Building2,
          options: orgUnitOptions,
          searchable: true,
        },
      ];
    }
    
    return [statusFacet];
  }, [stats, orgUnits]);
  
  // Sort options - stable reference
  const sortOptions: SortOption<string>[] = useMemo(() => [
    { field: 'submittedAt', label: 'Date Submitted', defaultDirection: 'desc' },
    { field: 'title', label: 'Title' },
    { field: 'status', label: 'Status' },
  ], []);
  
  // Render item callback - MUST be before any early returns
  const renderItem = useCallback((submission: IdeaSubmission, _props: ItemRenderProps) => {
    const orgUnit = submission.orgUnitId ? orgUnitMap.get(submission.orgUnitId) : null;
    const canShapeThis = !isEnterpriseLoaded || !submission.orgUnitId || canShapeIn(submission.orgUnitId);
    
    return (
      <SubmissionCard
        submission={submission}
        orgUnitName={orgUnit?.displayName}
        canShape={canShapeThis}
        canRoute={canRouteItems}
        isShaping={shapingId === submission.submissionId}
        onShape={() => handleShape(submission)}
        onRoute={(queueId) => handleRoute(submission.shapingItemId || submission.submissionId, queueId)}
        availableQueues={availableQueues}
        linkedCandidateId={submission.shapingItemId}
      />
    );
  }, [orgUnitMap, isEnterpriseLoaded, canShapeIn, canRouteItems, shapingId, handleShape, handleRoute, availableQueues]);
  
  // Access check - early returns AFTER all hooks
  if (!canTriage && !canShape) {
    return (
      <div className="space-y-6">
        <div className="card bg-[var(--mustard-dim)] border-[var(--mustard)]">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-[var(--mustard-dim)] rounded-full">
              <Lock className="h-6 w-6 text-[var(--mustard)]" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-[var(--mustard)] mb-2">Access Restricted</h2>
              <p className="text-sm text-[var(--mustard)] mb-2">
                You don't have permission to view the intake queue. This page requires the <strong>items.triage</strong> or <strong>items.edit</strong> permission.
              </p>
              <p className="text-xs text-[var(--mustard)]">
                Roles with this permission: Item Manager, Workspace Admin, Platform Admin
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="card">
          <p className="text-[var(--text-sec)]">Loading intake queue...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="space-y-6">
        <div className="card bg-[var(--coral-dim)] border-[var(--coral)]">
          <h2 className="text-lg font-semibold text-[var(--coral)] mb-2">Error</h2>
          <p className="text-sm text-[var(--coral)]">{error}</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        icon={<Users className="h-4 w-4 text-primary-600" />}
        iconBg="bg-primary-50"
        title="Intake Queue"
        subtitle="Review intake items, route them, and continue item work as needed"
        actions={!isEnterpriseLoaded ? (
          <button
            onClick={loadDemoData}
            className="text-xs px-3 py-1.5 bg-[var(--slate-dim)] text-[var(--text-sec)] hover:bg-[var(--slate-dim)] transition-colors"
          >
            Load Demo Org Data
          </button>
        ) : undefined}
      />
      
      {/* Enterprise Identity Notice */}
      {isEnterpriseLoaded && (
        <div className="bg-[var(--slate-dim)] border border-[var(--slate)] p-3">
          <div className="flex items-center gap-2 text-sm text-[var(--text-sec)]">
            <Building2 className="h-4 w-4 text-[var(--text-ter)]" />
            <span>
              Showing items from your accessible departments
            </span>
          </div>
        </div>
      )}
      
      {/* Filter Bar */}
      <div className="flex items-center gap-6 flex-wrap">
        {/* Queue Filter */}
        {availableQueues.length > 0 && (
          <div className="flex items-center gap-3">
            <label htmlFor="queue-filter" className="text-sm font-medium text-[var(--text-sec)]">
              Filter by Queue:
            </label>
            <select
              id="queue-filter"
              value={selectedQueueFilter}
              onChange={(e) => setSelectedQueueFilter(e.target.value)}
              className="px-3 py-1.5 border border-[var(--rule)] text-sm bg-[var(--bg-raised)] focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="all">All Queues</option>
              {availableQueues.map((queue) => (
                <option key={queue.id} value={queue.id}>{queue.label}</option>
              ))}
            </select>
          </div>
        )}
        
        {/* Triage Status Filter */}
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={showNonTriagedItems}
            onChange={(e) => setShowNonTriagedItems(e.target.checked)}
            className="rounded border-[var(--rule)] text-primary-600 focus:ring-primary-500"
          />
          <span className="text-[var(--text-sec)]">Include items pending triage</span>
        </label>
        
        {/* Item count */}
        <span className="text-sm text-[var(--text-ter)]">
          {filteredSubmissions.length} item{filteredSubmissions.length !== 1 ? 's' : ''}
        </span>
      </div>
      
      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <div className="card">
          <div className="flex items-center">
            <Inbox className="h-6 w-6 text-[var(--coral)] mr-3" />
            <div>
              <p className="text-sm font-medium text-[var(--text-ter)]">Pending Intake</p>
              <p className="text-2xl font-semibold text-[var(--text-pri)]">{stats.pending}</p>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center">
            <Hammer className="h-6 w-6 text-[var(--mustard)] mr-3" />
            <div>
              <p className="text-sm font-medium text-[var(--text-ter)]">In Item Work</p>
              <p className="text-2xl font-semibold text-[var(--text-pri)]">{stats.shaping}</p>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center">
            <Route className="h-6 w-6 text-[var(--text-ter)] mr-3" />
            <div>
              <p className="text-sm font-medium text-[var(--text-ter)]">Redirected</p>
              <p className="text-2xl font-semibold text-[var(--text-pri)]">{stats.redirected}</p>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center">
            <ListChecks className="h-6 w-6 text-[var(--text-ter)] mr-3" />
            <div>
              <p className="text-sm font-medium text-[var(--text-ter)]">Total in Queue</p>
              <p className="text-2xl font-semibold text-[var(--text-pri)]">{stats.total}</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Collection View */}
      <div className="card">
        <CollectionView
          items={filteredSubmissions}
          getId={(s) => s.submissionId}
          renderItem={renderItem}
          emptyVariant="ideas"
          emptyTitle="No items in the intake queue"
          emptyDescription="All submissions have been processed"
          facets={facets}
          searchFields={['title', 'description', 'submitterName', 'category']}
          searchPlaceholder="Search submissions..."
          sortOptions={sortOptions}
          defaultSort={{ field: 'submittedAt', direction: 'desc' }}
          viewModes={['list']}
          defaultViewMode="list"
        />
      </div>
    </div>
  );
}
